import { pageLoaders } from './lazyPages';
import { whenNetworkIdle } from '../services/networkActivity';

// Route prefetching.
//
// The sidebar is the only way an operator moves between sections, and every
// section is its own lazily loaded chunk. Without prefetching, the first click on
// "Payments" suspends the route, React unmounts the current page and shows a
// loader - which reads as "the whole page reloaded". Warming the chunk while the
// pointer is over the menu item (or right after login, while the browser is idle)
// makes the switch paint the new section instead of a spinner.
//
// Prefixes are matched longest-first so /network/hotspot wins over /network.
//
// Gap between successive background chunk warm-ups. Firing four `import()`s in
// the same tick put four bundle chunks ahead of the page's own API calls on the
// single HTTP/2 connection the app keeps open.
const PREFETCH_STAGGER_MS = 900;

const ROUTE_LOADERS = [
  { prefix: '/network/vlans', loader: pageLoaders.vlans },
  { prefix: '/network/hotspot', loader: pageLoaders.hotspot },
  { prefix: '/network/monitoring', loader: pageLoaders.monitoring },
  { prefix: '/network/router', loader: pageLoaders.routerDetails },
  { prefix: '/network', loader: pageLoaders.network },
  { prefix: '/packages-and-parents', loader: pageLoaders.packagesAndParents },
  { prefix: '/packages', loader: pageLoaders.packagesAndParents },
  { prefix: '/customers', loader: pageLoaders.customers },
  { prefix: '/billing/subscriptions', loader: pageLoaders.subscriptionDetails },
  { prefix: '/billing', loader: pageLoaders.billing },
  { prefix: '/payments', loader: pageLoaders.payments },
  { prefix: '/credit-ledger', loader: pageLoaders.creditLedger },
  { prefix: '/support', loader: pageLoaders.support },
  { prefix: '/reports', loader: pageLoaders.reports },
  { prefix: '/settings', loader: pageLoaders.settings },
  { prefix: '/inventory', loader: pageLoaders.inventory },
  { prefix: '/shop', loader: pageLoaders.shop },
  { prefix: '/traffic', loader: pageLoaders.customerTraffic },
  { prefix: '/renew', loader: pageLoaders.renewalLanding },
  { prefix: '/print/isp', loader: pageLoaders.printIspDocument },
  { prefix: '/print', loader: pageLoaders.printDocument },
  // The dashboard is the index route (`/`). It only ever matches exactly, because
  // the prefix test appends a slash, so it can never shadow a section below it.
  { prefix: '/', loader: pageLoaders.dashboard }
];

// Chunks already (being) fetched. A rejected load is forgotten so a later click
// can retry instead of being permanently stuck behind a failed promise.
const settled = new Set();
const inflight = new Map();

function resolveLoader(pathname) {
  if (!pathname) return null;
  const path = String(pathname).split('?')[0].split('#')[0];
  const candidates = ROUTE_LOADERS.filter(
    (entry) => path === entry.prefix || path.startsWith(`${entry.prefix}/`)
  );
  if (!candidates.length) return null;
  return candidates.sort((a, b) => b.prefix.length - a.prefix.length)[0];
}

function load(entry) {
  if (!entry || !entry.loader) return null;
  const key = entry.prefix;
  if (settled.has(key)) return null;
  if (inflight.has(key)) return inflight.get(key);

  const promise = entry.loader()
    .then((mod) => {
      settled.add(key);
      inflight.delete(key);
      return mod;
    })
    .catch((error) => {
      inflight.delete(key);
      throw error;
    });

  inflight.set(key, promise);
  // Prefetch failures are never fatal: the click falls back to a normal lazy load.
  promise.catch(() => {});
  return promise;
}

/** Warm the chunk for a route path (safe to call on every hover). */
export function prefetchRoute(pathname) {
  const entry = resolveLoader(pathname);
  if (!entry) return null;
  try {
    return load(entry);
  } catch (_) {
    return null;
  }
}

/**
 * Warm the sections an operator opens most, once the page has actually settled.
 *
 * WHY THIS NO LONGER TRUSTS `requestIdleCallback`
 * -----------------------------------------------
 * A reload of /payments used to take up to 8 s. The captured trace
 * (`download.devtools`, analysed by scripts/analyze-devtools-trace.js) shows the
 * current page's own chunk batch answering in ~100 ms and `/api/payments`
 * answering immediately - the rest of the timeline was THIS function: four
 * staggered waves of speculative chunks at +3.5 s, +4.0 s, +4.9 s, +5.8 s and
 * +7.7 s, queued on the same connection behind the page's own requests.
 *
 * `requestIdleCallback` was the wrong signal. On a slow link the main thread is
 * idle precisely because it is *waiting on the wire*, so the warm-up started
 * exactly when the page still needed the bandwidth.
 *
 * What it does now:
 *   - waits until no API request is in flight and none has finished for a while
 *     (`whenNetworkIdle`, fed by services/api.js);
 *   - never re-downloads the route already on screen;
 *   - still staggers the rest, and still skips metered/slow connections.
 *
 * Prefetching remains best-effort: if the wait times out the warm-up still runs,
 * and if it never runs the first click costs one lazy load - exactly what the app
 * did before this helper existed.
 */
// `/reports` is warmed as well. Its route chunk is ~24 kB and is a cache miss on
// the first visit after every deploy; the 2026-11-03 trace shows what an uncached
// chunk costs on the production link (16.8 s for a 49 kB one, because it has to
// queue behind the page's own requests on the same connection). Downloading it
// while the dashboard is quiet costs the same bytes and removes that wait from
// the click.
const DEFAULT_ROUTES = ['/', '/customers', '/billing', '/payments', '/shop', '/reports'];

// Quiet period required before speculative downloads start. Long enough that the
// page's last request has landed and its render has finished.
const IDLE_QUIET_MS = (() => {
  const parsed = Number.parseInt(String(process.env.REACT_APP_PREFETCH_IDLE_QUIET_MS ?? ''), 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 1500;
})();

const isCurrentRoute = (path, currentPath) => {
  if (!currentPath) return false;
  if (path === '/') return currentPath === '/';
  return currentPath === path || currentPath.startsWith(`${path}/`);
};

export function prefetchCommonRoutes(paths = DEFAULT_ROUTES) {
  if (typeof window === 'undefined') return;

  // Idle means the main thread is idle, not that the network is. On high-loss
  // connections these speculative chunks can delay the active page's API calls,
  // so callers may still opt out explicitly. Hover/focus prefetch remains the
  // immediate path and background warming now defaults on for healthy links.
  if (String(process.env.REACT_APP_PREFETCH_COMMON_ROUTES || '').toLowerCase() === 'false') return;

  const connection = window.navigator?.connection
    || window.navigator?.mozConnection
    || window.navigator?.webkitConnection;
  if (connection) {
    if (connection.saveData === true) return;
    const effectiveType = String(connection.effectiveType || '').toLowerCase();
    if (effectiveType === 'slow-2g' || effectiveType === '2g' || effectiveType === '3g') return;
  }

  const currentPath = window.location?.pathname || '';
  const targets = paths.filter((path) => !isCurrentRoute(path, currentPath));
  if (!targets.length) return;

  const run = async () => {
    await whenNetworkIdle({ quietMs: IDLE_QUIET_MS });
    targets.forEach((path, index) => {
      if (index === 0) {
        prefetchRoute(path);
        return;
      }
      setTimeout(() => prefetchRoute(path), index * PREFETCH_STAGGER_MS);
    });
  };

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: 4000 });
  } else {
    setTimeout(run, 1500);
  }
}

export const _internal = { resolveLoader, settled, inflight, isCurrentRoute, IDLE_QUIET_MS };
